import {
  MeetingContainerAPI,
  mediaSessionIsHidden,
  useMeetingSelector,
  useVideoSignalDetection,
} from "@kalyzee/kast-web-sdk";
import { MeetingSessionMedia, MeetingSessionMediaStatus } from "@kalyzee/kast-web-sdk/websocket";
import React, { useEffect, useMemo, useRef } from "react";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { logger } from "../../helpers/logger";
import { MeetingSourceExtended, useMeetingSources } from "../../hooks/meeting";

interface WatcherProps {
  meetingAPI: MeetingContainerAPI;
  sessionId: string;
  mediaSession: MeetingSessionMedia;
  stream: MediaStream;
  options: NonNullable<MeetingSourceExtended["signalDetection"]>;
}

/**
 * Watches one local stream and keeps its media status in sync with the signal:
 * a source that goes away (nothing plugged in, card sending its test pattern)
 * hides the media, and it comes back on its own once the signal returns.
 */
const Watcher = React.memo(({ meetingAPI, sessionId, mediaSession, stream, options }: WatcherProps) => {
  const { enabled, ...detectorOptions } = options;
  const data = useVideoSignalDetection(stream, detectorOptions);
  const { hasSignal } = data;
  console.log("useVideoSignalDetection - Data : ", data);
  const hidden = mediaSessionIsHidden(mediaSession);

  /**
   * Last signal value acted upon. Only a *change* of signal drives the status,
   * so toggling the switch by hand sticks: the detection keeps watching, says
   * nothing while its verdict is unchanged, and takes over again at the next
   * transition.
   */
  const appliedSignalRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    // `undefined` while the detector has yet to make up its mind.
    if (hasSignal === undefined) return;
    if (hasSignal === appliedSignalRef.current) return;
    appliedSignalRef.current = hasSignal;
    // Manual state may already match the new verdict — nothing left to do.
    if (hasSignal !== hidden) return;
    logger.log(`[videoSignal] ${mediaSession.name}: signal ${hasSignal ? "back" : "lost"}, updating media status`);
    meetingAPI.updater.medias.update(sessionId, mediaSession.id, {
      status: hasSignal ? MeetingSessionMediaStatus.ENABLED : MeetingSessionMediaStatus.HIDDEN,
    });
  }, [hasSignal, hidden, meetingAPI, sessionId, mediaSession.id, mediaSession.name]);

  return null;
});

export interface VideoSignalAutoHideProps {
  meetingAPI: MeetingContainerAPI;
}

/**
 * Mounts one watcher per configured source that asked for signal detection
 * (`sources[].signalDetection.enabled` in the settings). Renders nothing.
 */
export const VideoSignalAutoHide = React.memo(({ meetingAPI }: VideoSignalAutoHideProps) => {
  const sources = useMeetingSources();
  const selfStreams = useAppStoreSelector(state => state.selfStreams);
  const [session] = useMeetingSelector(meetingAPI, "session");

  const watched = useMemo(() => {
    if (!session) return [];
    return sources.flatMap((source) => {
      if (!source.signalDetection?.enabled || !source.name) return [];
      const stream = selfStreams[source.name]?.getMediaStream();
      // Medias carry the source name, which is what ties them to the stream.
      const mediaSession = session.medias?.find(m => m.name === source.name);
      if (!stream || !mediaSession) return [];
      return [{ source, stream, mediaSession }];
    });
  }, [sources, selfStreams, session]);

  if (!session) return null;

  return (
    <>
      {watched.map(({ source, stream, mediaSession }) => (
        <Watcher
          key={mediaSession.id}
          meetingAPI={meetingAPI}
          sessionId={session.id}
          mediaSession={mediaSession}
          stream={stream}
          options={source.signalDetection!}
        />
      ))}
    </>
  );
});
